import { differenceInDays, format, isSameDay, set, startOfDay, subDays } from 'date-fns';
import { ApexChartSeries } from '../types';

export const normaliseChartLineData = (
  items?: ApexChartSeries[],
  withCurrentDate = true,
): ApexChartSeries[] => {
  if (!items) return [];

  const allTimePoints = (items || []).reduce(
    (acc, { data = [] }) => [
      ...acc,
      ...data
        .sort((a, b) => new Date(a.x).getTime() - new Date(b.x).getTime())
        .filter(({ y }, index, arr) => {
          const prevElement = arr[index - 1];

          return index === 0 || index === arr.length - 1 || y !== prevElement?.y;
        })
        .map(({ x }) => x),
    ],
    [] as string[],
  );

  let uniqSortedTimePoints = [...new Set(allTimePoints)].sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime(),
  );

  if (uniqSortedTimePoints.length === 1) {
    const now = new Date();
    uniqSortedTimePoints[0] = set(new Date(uniqSortedTimePoints[0]), {
      hours: now.getHours(),
      minutes: now.getMinutes(),
      seconds: now.getSeconds(),
      milliseconds: now.getMilliseconds(),
    }).toString();

    uniqSortedTimePoints.unshift(startOfDay(new Date(uniqSortedTimePoints[0])).toString());
  }

  const lastDate =
    uniqSortedTimePoints?.at(-1) &&
    !isSameDay(new Date(uniqSortedTimePoints?.at(-1) || subDays(new Date(), 1)), new Date()) &&
    differenceInDays(new Date(uniqSortedTimePoints?.at(-1) || subDays(new Date(), 1)), new Date()) <
      0 &&
    withCurrentDate
      ? startOfDay(new Date()).toString()
      : undefined;

  uniqSortedTimePoints = lastDate ? [...uniqSortedTimePoints, lastDate] : uniqSortedTimePoints;

  const chartDate: ApexChartSeries[] = items
    .filter(({ data }) => data?.length)
    .map(({ name, data = [], type, ...other }) => {
      const lastValue = lastDate ? data.at(-1) : undefined;
      return {
        ...other,
        name,
        type,
        data: uniqSortedTimePoints.map(date => {
          const dateObj = new Date(date);

          const isMidnightUTC =
            format(dateObj, 'H:m:s') === '0:0:0' ||
            format(dateObj, 'H:m:s') === '3:0:0' ||
            format(dateObj, 'H:m:s') === '2:0:0';

          const formattedDate = isMidnightUTC
            ? format(dateObj, 'dd-MM-yyyy')
            : format(dateObj, 'dd-MM-yyyy HH:mm:ss');

          return {
            x: formattedDate,
            y:
              lastValue &&
              lastDate &&
              format(new Date(lastDate), 'dd-MM-yyyy') === format(dateObj, 'dd-MM-yyyy')
                ? lastValue?.y
                : data.find(
                    ({ x }) =>
                      format(new Date(x), 'dd-MM-yyyy HH:mm:ss') ===
                      format(new Date(date), 'dd-MM-yyyy HH:mm:ss'),
                  )?.y ||
                  data.find(
                    ({ x }) =>
                      format(new Date(x), 'dd-MM-yyyy') === format(new Date(date), 'dd-MM-yyyy'),
                  )?.y ||
                  0,
          };
        }),
      };
    });

  return chartDate;
};
